import { z } from 'zod';

/**
 * Batch summary of travelers by legacy id, for operator surfaces that already
 * hold the id (cirium legs, cards). Fail-soft per item on the server: a missing
 * company or VIP record yields nulls, never a dropped item.
 */
export const CustomerSummarySchema = z.object({
  id: z.string().min(1),
  publicId: z.string().min(1),
  name: z.string(),
  companyId: z.string().nullable(),
  companyName: z.string().nullable(),
  phone: z.string().nullable(),
  vip: z.boolean(),
  vipLevel: z.string().nullable(),
});
export type CustomerSummary = z.infer<typeof CustomerSummarySchema>;

export const CUSTOMER_SUMMARIES_MAX_IDS = 300;

/**
 * Legacy customer ids are canonical decimal integers (the source adapter maps
 * every id through `Number(id)`), so a lookalike string like `'01'` or
 * `'user-01'` must be rejected here rather than silently resolving to the
 * wrong source row (`'01'` → `Number('01')` → id `1`) and then dropping out of
 * the response because the returned row's `id` no longer matches the request.
 */
const CanonicalCustomerIdSchema = z
  .string()
  .regex(/^(0|[1-9]\d*)$/, 'must be a canonical decimal integer id')
  .refine((id) => Number.isSafeInteger(Number(id)), 'must be a safe integer id');

export const CustomerSummariesRequestSchema = z.object({
  ids: z.array(CanonicalCustomerIdSchema).min(1).max(CUSTOMER_SUMMARIES_MAX_IDS),
});
export type CustomerSummariesRequest = z.infer<typeof CustomerSummariesRequestSchema>;

export const CustomerSummariesResponseSchema = z.object({
  summaries: z.array(CustomerSummarySchema),
});
export type CustomerSummariesResponse = z.infer<typeof CustomerSummariesResponseSchema>;

/** `POST /internal/customers/summaries/by-email`: o tamanho de uma planilha de lote. */
const CUSTOMERS_BY_EMAIL_MAX = 500;

/** Viajantes de UM cliente (`companyId`) por e-mail. Um e-mail igual em outro cliente não vaza. */
export const CustomersByEmailRequestSchema = z
  .object({
    companyId: z.string().min(1),
    emails: z
      .array(z.string().trim().toLowerCase().min(1).max(320))
      .min(1)
      .max(CUSTOMERS_BY_EMAIL_MAX),
  })
  .strict();

const CustomerEmailMatchSchema = z.object({
  id: z.string().min(1),
  name: z.string(),
  email: z.string(),
  phone: z.string().nullable(),
});
export type CustomerEmailMatch = z.infer<typeof CustomerEmailMatchSchema>;

/** E-mail sem viajante no cliente fica de fora; ordem não prometida (o chamador indexa por `email`). */
export const CustomersByEmailResponseSchema = z.object({
  matches: z.array(CustomerEmailMatchSchema),
});
